import { FC, useState, useRef } from 'react';
import { useTimeRecordForm } from '../TimeRecordForm/useTimeRecordForm';
import { format } from 'date-fns';
import TimeInput from '../TimeInput';
import { supabase } from '../../lib/supabaseClient';
import CameraCapture from '../CameraCapture';

const DTR: FC = () => {
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [isProcessingPhoto, setIsProcessingPhoto] = useState(false);
  const [photoToast, setPhotoToast] = useState<string | null>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const {
    formState,
    error,
    success,
    loading,
    isDirty,
    hasRecord,
    handleTimeChange,
    handleDateChange,
    handleSubmit: originalHandleSubmit,
    handleReset,
    handleDelete
  } = useTimeRecordForm(new Date().toISOString().split('T')[0]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      console.log('Saving record...', formState);
      
      // Validate time pairs first
      if ((formState.amIn && !formState.amOut) || (!formState.amIn && formState.amOut)) {
        setSaveMessage('Error: Morning time in/out must be filled in pairs');
        return;
      }
      
      if ((formState.pmIn && !formState.pmOut) || (!formState.pmIn && formState.pmOut)) {
        setSaveMessage('Error: Afternoon time in/out must be filled in pairs');
        return;
      }
      
      if ((formState.otIn && !formState.otOut) || (!formState.otIn && formState.otOut)) {
        setSaveMessage('Error: Overtime time in/out must be filled in pairs');
        return;
      }

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      
      const record: any = {
        record_date: formState.recordDate,
        sales_amount: formState.salesAmount || 0,
        ca_amount: formState.caAmount || 0,
        user_id: user?.id
      };

      // Only add time fields if they have values
      if (formState.amIn) record.am_in = formState.amIn;
      if (formState.amOut) record.am_out = formState.amOut;
      if (formState.pmIn) record.pm_in = formState.pmIn;
      if (formState.pmOut) record.pm_out = formState.pmOut;
      if (formState.otIn) record.ot_in = formState.otIn;
      if (formState.otOut) record.ot_out = formState.otOut;
      
      // Only add comments if not empty
      if (formState.comments) record.comments = formState.comments;

      const { error: supabaseError } = await supabase
        .from('time_records')
        .upsert(record, {
          onConflict: 'record_date,user_id',
          ignoreDuplicates: false
        });

      if (supabaseError) {
        console.error('Supabase error:', supabaseError);
        setSaveMessage(`Error: ${supabaseError.message}`);
      } else {
        setSaveMessage('Record saved successfully!');
        console.log('Record saved successfully!');
      }
    } catch (error: any) {
      console.error('Unexpected error:', error);
      setSaveMessage(`Error: ${error.message}`);
    }
  };

  const handlePhotoCapture = (url: string) => {
    setPhotoToast('Photo uploaded successfully!');
    setTimeout(() => setPhotoToast(null), 3000);
  };

  return (
    <div className="max-w-2xl mx-auto px-4">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-4 sm:p-6">
          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Daily Time Record</h2>
              <p className="text-sm text-gray-500 mt-1">
                {format(new Date(formState.recordDate), 'EEEE, MMMM d, yyyy')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {/* Camera Icon Button */}
              <button
                type="button"
                onClick={() => cameraInputRef.current?.click()}
                disabled={isProcessingPhoto}
                className="p-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors group relative disabled:opacity-50"
                title="Take a selfie snapshot"
              >
                {isProcessingPhoto ? (
                  <svg className="animate-spin w-6 h-6 text-blue-600" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                ) : (
                  <svg
                    className="w-6 h-6 text-gray-500 group-hover:text-blue-600 transition-colors"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                )}
              </button>
              <input
                type="date"
                value={formState.recordDate}
                onChange={(e) => handleDateChange(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Time Records Section */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Morning Section */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-gray-900 mb-4 flex items-center">
                  <svg className="w-4 h-4 mr-2 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" />
                  </svg>
                  Morning
                </h3>
                <div className="space-y-4">
                  <TimeInput
                    label="Time In"
                    name="amIn"
                    value={formState.amIn}
                    onChange={handleTimeChange}
                    disabled={loading}
                  />
                  <TimeInput
                    label="Time Out"
                    name="amOut"
                    value={formState.amOut}
                    onChange={handleTimeChange}
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Afternoon Section */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-gray-900 mb-4 flex items-center">
                  <svg className="w-4 h-4 mr-2 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 2a8 8 0 100 16 8 8 0 000-16zm0 14a6 6 0 110-12 6 6 0 010 12zm1-6a1 1 0 10-2 0v2a1 1 0 00.553.894l1.5.866a1 1 0 001-1.732L11 10V8z" clipRule="evenodd" />
                  </svg>
                  Afternoon
                </h3>
                <div className="space-y-4">
                  <TimeInput
                    label="Time In"
                    name="pmIn"
                    value={formState.pmIn}
                    onChange={handleTimeChange}
                    disabled={loading}
                  />
                  <TimeInput
                    label="Time Out"
                    name="pmOut"
                    value={formState.pmOut}
                    onChange={handleTimeChange}
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Overtime Section */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-gray-900 mb-4 flex items-center">
                  <svg className="w-4 h-4 mr-2 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                  </svg>
                  Overtime
                </h3>
                <div className="space-y-4">
                  <TimeInput
                    label="Time In"
                    name="otIn"
                    value={formState.otIn}
                    onChange={handleTimeChange}
                    disabled={loading}
                  />
                  <TimeInput
                    label="Time Out"
                    name="otOut"
                    value={formState.otOut}
                    onChange={handleTimeChange}
                    disabled={loading}
                  />
                </div>
              </div>
            </div>

            {/* Amounts Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Sales Amount */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-gray-900 mb-4 flex items-center">
                  <svg className="w-4 h-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                  </svg>
                  Sales Amount
                </h3>
                <div className="relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500">₱</span>
                  </div>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formState.salesAmount || ''}
                    onChange={(e) => handleTimeChange('salesAmount', e.target.value)}
                    disabled={loading}
                    className="block w-full pl-7 pr-12 py-2 rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:bg-gray-50"
                    placeholder="0.00"
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <span className="text-gray-500">PHP</span>
                  </div>
                </div>
              </div>

              {/* CA Amount */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-gray-900 mb-4 flex items-center">
                  <svg className="w-4 h-4 mr-2 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                  </svg>
                  CA Amount
                </h3>
                <div className="relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500">₱</span>
                  </div>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formState.caAmount || ''}
                    onChange={(e) => handleTimeChange('caAmount', e.target.value)}
                    disabled={loading}
                    className="block w-full pl-7 pr-12 py-2 rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:bg-gray-50"
                    placeholder="0.00"
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <span className="text-gray-500">PHP</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Comments Section */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-sm font-medium text-gray-900 mb-4 flex items-center">
                <svg className="w-4 h-4 mr-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 13V5a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2h3l3 3 3-3h3a2 2 0 002-2zM5 7a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H6z" clipRule="evenodd" />
                </svg>
                Comments/Notes (Optional)
              </h3>
              <textarea
                value={formState.comments}
                onChange={(e) => handleTimeChange('comments', e.target.value)}
                disabled={loading}
                rows={3}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:bg-gray-50"
                placeholder="Add any comments or notes here..."
              />
            </div>

            {/* Messages and Action Buttons */}
            <div className="space-y-4">
              {saveMessage && (
                <div className={`rounded-md p-4 ${
                  saveMessage.includes('Error') 
                    ? 'bg-red-50 border border-red-200 text-red-700' 
                    : 'bg-green-50 border border-green-200 text-green-700'
                }`}>
                  {saveMessage}
                </div>
              )}

              {(error || success) && (
                <div className="rounded-md transition-all duration-300 ease-in-out">
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md flex items-start">
                      <svg className="h-5 w-5 text-red-400 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm8-7a1 1 0 10-2 0v2a1 1 0 00.866.5l1.5-2a1 1 0 000-1.5L18 5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1h2.586l-2.29 2.29a1 1 0 001.414 1.414L8 10.414l2.29 2.29a1 1 0 001.414-1.414L10.414 8l2.29-2.29a1 1 0 00-1.414-1.414L8 6.586 5.586 5a1 1 0 000 1.414l2.29 2.29a1 1 0 001.414 1.414z" clipRule="evenodd" />
                      </svg>
                      <span className="ml-3">{error}</span>
                    </div>
                  )}
                  {success && (
                    <div className="bg-green-50 border border-green-200 text-green-700 p-4 rounded-md flex items-start">
                      <svg className="h-5 w-5 text-green-400 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="ml-3">Record saved successfully!</span>
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={loading || !isDirty}
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Save Record'}
                </button>
                
                {isDirty && (
                  <button
                    type="button"
                    onClick={handleReset}
                    disabled={loading}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                  >
                    Reset
                  </button>
                )}
                
                {hasRecord && (
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={loading}
                    className="px-4 py-2 text-red-700 bg-red-100 rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Hidden Camera Input - triggers native camera app */}
      <CameraCapture
        ref={cameraInputRef}
        onCapture={handlePhotoCapture}
        onProcessing={setIsProcessingPhoto}
        recordDate={formState.recordDate}
      />

      {/* Photo Upload Toast */}
      {photoToast && (
        <div className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-bottom-4 fade-in duration-300">
          <div className="bg-green-600 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            {photoToast}
          </div>
        </div>
      )}
    </div>
  );
};

export default DTR;